import LayoutHR from "layouts/layout-hr";
import React, { useCallback, useMemo, useState, useEffect } from "react";
import { Dropdown } from "react-bootstrap";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";

import { useCanvasHandler } from "hooks/useCanvasHandler";
import { deleteRequest, getRequest } from "services/api";
import Swal from "sweetalert2";
import MasterDataFormV2 from "../MasterDataFormV2";
import {
  SalarySettingProvider,
  useSalarySetting,
} from "./context/SalarySettingContext";
import { useUser } from "contexts/v1/masterData/UserContext";
import { getDevelopmentBanner } from "utils/devNotice";
import { useNavigate } from "react-router-dom";
import { API_URL } from "utils/constants";
import "./salary-setting-page.css";

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const number = String(value).replace(/\D/g, "");
  if (!number) return "";
  return new Intl.NumberFormat("id-ID").format(Number(number));
};

const unformatNumber = (value) => String(value || "").replace(/\D/g, "");

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const toOptionList = (items = []) => items.map((item) => ({
  value: item.id || item.value,
  label: item.name || item.label || item,
}));

const getIds = (items = []) => items.map((item) => item.value || item.id || item).filter(Boolean);

const salaryMasterConfigs = {
  insurances: {
    endpoint: "/ahr/insurances",
    label: "Insurance",
    defaultType: "health",
    typeOptions: [
      { value: "health", label: "Health" },
      { value: "life", label: "Life" },
      { value: "vehicle", label: "Vehicle" },
      { value: "property", label: "Property" },
    ],
  },
  facilities: {
    endpoint: "/ahr/facilities",
    label: "Facility",
    defaultType: "once",
    typeOptions: [
      { value: "once", label: "Once" },
      { value: "multiple", label: "Multiple" },
    ],
  },
  deductions: {
    endpoint: "/ahr/deductions",
    label: "Deduction",
    defaultType: "once",
    typeOptions: [
      { value: "once", label: "Once" },
      { value: "multiple", label: "Multiple" },
    ],
  },
};

function SalarySettingContent() {
  const {
    salarySettings,
    filterOptions,
    handleUserFilter,
    refetch,

    Pagination,
    loadInsuranceOptions,
    loadFacilityOptions,
    loadDeductionOptions,
  } = useSalarySetting();
  
  const { users } = useUser();
  const navigate = useNavigate();

  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [insuranceOptions, setInsuranceOptions] = useState([]);
  const [facilityOptions, setFacilityOptions] = useState([]);
  const [deductionOptions, setDeductionOptions] = useState([]);
  const [viewMode, setViewMode] = useState("list");
  const [excelRows, setExcelRows] = useState([]);
  const [excelSettings, setExcelSettings] = useState({ data: [], loading: false });
  const [savingRows, setSavingRows] = useState({});
  const [selectedSettingIds, setSelectedSettingIds] = useState([]);

  const { handleError, handleSuccessToast } = useCanvasHandler();
  const visibleSettingIds = useMemo(() => (salarySettings.data || []).map((item) => item.id).filter(Boolean), [salarySettings.data]);
  const selectedVisibleCount = visibleSettingIds.filter((id) => selectedSettingIds.includes(id)).length;
  const isAllVisibleSelected = visibleSettingIds.length > 0 && selectedVisibleCount === visibleSettingIds.length;

  useEffect(() => {
    setSelectedSettingIds([]);
  }, [salarySettings.data, viewMode]);

  const fetchExcelSalarySettings = useCallback(async () => {
    setExcelSettings((prev) => ({ ...prev, loading: true }));
    try {
      const response = await getRequest("/ahr/salary-settings?include=user,insurances,facilities,deductions&all=1&limit=1000");
      setExcelSettings({
        data: response.success ? response.data || [] : [],
        loading: false,
      });
    } catch (error) {
      setExcelSettings({ data: [], loading: false });
      Swal.fire({
        icon: "error",
        title: "Failed to load salary settings",
        text: error.message || "Please try again.",
        confirmButtonColor: "#dc3545",
      });
    }
  }, []);

  // Convert users from UserProvider to options format
  useEffect(() => {
    if (users.data && users.data.length > 0) {
      const userOptionsData = users.data.map((user) => ({
        value: user.id,
        label: user.name,
      }));
      setUserOptions(userOptionsData);
    }
  }, [users.data]);

  useEffect(() => {
    const settingsSource = viewMode === "excel" ? excelSettings.data : salarySettings.data;
    const settingsByUserId = new Map((settingsSource || []).map((item) => [item.user_id, item]));
    const rows = (users.data || []).map((user) => {
      const setting = settingsByUserId.get(user.id);
      return {
        rowKey: setting?.id || user.id,
        id: setting?.id || null,
        user_id: user.id,
        user_name: user.name,
        department: user.department?.name || "-",
        position: user.position?.name || "-",
        salary: setting?.salary ? String(setting.salary) : "",
        salary_display: formatNumber(setting?.salary),
        insurances: toOptionList(setting?.insurances || []),
        facilities: toOptionList(setting?.facilities || []),
        deductions: toOptionList(setting?.deductions || []),
        is_active: setting?.is_active === undefined ? true : Boolean(Number(setting.is_active)),
        dirty: false,
      };
    });
    setExcelRows(rows);
  }, [excelSettings.data, salarySettings.data, users.data, viewMode]);

  useEffect(() => {
    if (viewMode !== "excel") return;
    fetchExcelSalarySettings();
  }, [fetchExcelSalarySettings, viewMode]);

  const groupedExcelRows = useMemo(() => {
    const groups = new Map();
    excelRows.forEach((row) => {
      const departmentName = row.department || "Tanpa Department";
      if (!groups.has(departmentName)) groups.set(departmentName, []);
      groups.get(departmentName).push(row);
    });
    return Array.from(groups.entries());
  }, [excelRows]);

  // Load default options on component mount
  useEffect(() => {
    const loadDefaultOptions = async () => {
      try {
        const [insurances, facilities, deductions] = await Promise.all([
          loadInsuranceOptions(),
          loadFacilityOptions(),
          loadDeductionOptions(),
        ]);
        setInsuranceOptions(insurances);
        setFacilityOptions(facilities);
        setDeductionOptions(deductions);
      } catch (error) {
        console.error("Error loading default options:", error);
      }
    };

    loadDefaultOptions();
  }, [loadInsuranceOptions, loadFacilityOptions, loadDeductionOptions]);

  // Handle create new salary setting
  const handleCreate = () => {
    setShowModal(true);
    setEditingItem(null);
  };

  // Handle edit salary setting
  const handleEdit = async (item) => {
    // Transform the data for form editing
    const transformedItem = {
      ...item,
      // Convert arrays of objects to arrays of IDs for multiselect
      insurances: item.insurances?.map(insurance => insurance.id) || [],
      facilities: item.facilities?.map(facility => facility.id) || [],
      deductions: item.deductions?.map(deduction => deduction.id) || [],
    };

    // Transform user_id to select object if needed
    if (userOptions.length > 0 && item.user_id) {
      const user = userOptions.find((u) => u.value === item.user_id);
      if (user) {
        transformedItem.user_id = user;
      }
    }

    // For multiselect fields, we need to load the selected options
    // and set them as objects so AsyncSelect can display them properly
    if (item.insurances && item.insurances.length > 0) {
      const insuranceIds = item.insurances.map(insurance => insurance.id);
      const selectedInsurances = insuranceOptions.filter(option =>
        insuranceIds.includes(option.value)
      );
      if (selectedInsurances.length > 0) {
        transformedItem.insurances = selectedInsurances;
      }
    }

    if (item.facilities && item.facilities.length > 0) {
      const facilityIds = item.facilities.map(facility => facility.id);
      const selectedFacilities = facilityOptions.filter(option =>
        facilityIds.includes(option.value)
      );
      if (selectedFacilities.length > 0) {
        transformedItem.facilities = selectedFacilities;
      }
    }

    if (item.deductions && item.deductions.length > 0) {
      const deductionIds = item.deductions.map(deduction => deduction.id);
      const selectedDeductions = deductionOptions.filter(option =>
        deductionIds.includes(option.value)
      );
      if (selectedDeductions.length > 0) {
        transformedItem.deductions = selectedDeductions;
      }
    }

    setEditingItem(transformedItem);
    setShowModal(true);
  };

  // Handle delete salary setting
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await deleteRequest(`/ahr/salary-settings/${id}`);

        if (response.success) {
          handleSuccessToast(true, "Salary setting deleted successfully");
          refetch(); // Refresh the data
        } else {
          Swal.fire({
            title: "Error",
            text: response.message || "Failed to delete salary setting",
            icon: "error",
            confirmButtonText: "OK",
            confirmButtonColor: "#dc3545"
          });
        }
      } catch (error) {
        console.error("Delete error:", error);
        Swal.fire({
          title: "Error",
          text: "Failed to delete salary setting. Please try again.",
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonColor: "#dc3545"
        });
      }
    }
  };

  const handleToggleSettingSelection = (id) => {
    setSelectedSettingIds((prev) => (
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    ));
  };

  const handleToggleSelectVisibleSettings = () => {
    setSelectedSettingIds((prev) => {
      const withoutVisible = prev.filter((id) => !visibleSettingIds.includes(id));
      return isAllVisibleSelected ? withoutVisible : [...withoutVisible, ...visibleSettingIds];
    });
  };

  const handleBulkDelete = async () => {
    if (selectedSettingIds.length === 0) return;

    const result = await Swal.fire({
      title: `Delete ${selectedSettingIds.length} salary settings?`,
      text: "Data salary setting yang dipilih akan dihapus.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete selected",
    });

    if (!result.isConfirmed) return;

    try {
      Swal.fire({
        title: "Deleting selected salary settings...",
        text: "Mohon tunggu sebentar.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const results = [];
      for (const id of selectedSettingIds) {
        // Hapus berurutan agar API tetap ringan saat bulk action.
        // eslint-disable-next-line no-await-in-loop
        results.push(await deleteRequest(`/ahr/salary-settings/${id}`));
      }

      const failed = results.filter((response) => !response?.success);
      if (failed.length > 0) {
        throw new Error(`${failed.length} salary settings failed to delete`);
      }

      Swal.close();
      handleSuccessToast(true, `${selectedSettingIds.length} salary settings deleted successfully`);
      setSelectedSettingIds([]);
      refetch();
    } catch (error) {
      Swal.close();
      handleError(error);
    }
  };

  // Handle form success
  const handleFormSuccess = () => {
    setShowModal(false);
    setEditingItem(null);
    refetch();
    handleSuccessToast(true, "Salary setting saved successfully");
  };

  // Handle form error
  const handleFormError = (error) => {
    handleError(error);
  };

  // Close modal
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const updateExcelRow = (rowKey, changes) => {
    setExcelRows((prevRows) => prevRows.map((row) => (
      row.rowKey === rowKey ? { ...row, ...changes, dirty: true } : row
    )));
  };

  const addOptionToState = (fieldName, option) => {
    const updater = (options) => {
      if (options.some((item) => item.value === option.value)) return options;
      return [...options, option].sort((a, b) => a.label.localeCompare(b.label));
    };

    if (fieldName === "insurances") setInsuranceOptions(updater);
    if (fieldName === "facilities") setFacilityOptions(updater);
    if (fieldName === "deductions") setDeductionOptions(updater);
  };

  const createSalaryMasterOption = async (fieldName, inputValue) => {
    const config = salaryMasterConfigs[fieldName];
    if (!config) return null;

    const typeOptionsHtml = config.typeOptions
      .map((option) => `<option value="${option.value}" ${option.value === config.defaultType ? "selected" : ""}>${option.label}</option>`)
      .join("");

    const result = await Swal.fire({
      title: `Create ${config.label}`,
      html: `
        <div style="text-align:left">
          <label class="form-label">Name</label>
          <input id="salary-master-name" class="swal2-input" style="margin:4px 0 12px;width:100%" value="${escapeHtml(inputValue)}">
          <label class="form-label">Price</label>
          <input id="salary-master-price" class="swal2-input" style="margin:4px 0 12px;width:100%" type="number" min="0" placeholder="0">
          <label class="form-label">Type</label>
          <select id="salary-master-type" class="swal2-select" style="margin:4px 0 0;width:100%">${typeOptionsHtml}</select>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Create & Select",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#206bc4",
      focusConfirm: false,
      preConfirm: () => {
        const name = document.getElementById("salary-master-name")?.value?.trim();
        const price = document.getElementById("salary-master-price")?.value;
        const type = document.getElementById("salary-master-type")?.value;
        if (!name) {
          Swal.showValidationMessage("Name wajib diisi");
          return false;
        }
        if (price === "" || Number(price) < 0) {
          Swal.showValidationMessage("Price wajib diisi minimal 0");
          return false;
        }
        return { name, price, type };
      },
    });

    if (!result.isConfirmed || !result.value) return null;

    const formData = new FormData();
    formData.append("name", result.value.name);
    formData.append("price", result.value.price);
    formData.append("type", result.value.type);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}${config.endpoint}`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const payload = await response.json();
      if (!response.ok || payload.success === false) throw payload;

      const item = payload.data || {};
      const option = {
        value: item.id,
        label: item.name || result.value.name,
      };
      addOptionToState(fieldName, option);
      handleSuccessToast(true, `${config.label} berhasil dibuat`);
      return option;
    } catch (error) {
      handleError(error);
      return null;
    }
  };

  const handleCreateSalaryMasterOption = async (rowKey, fieldName, inputValue) => {
    const option = await createSalaryMasterOption(fieldName, inputValue);
    if (!option) return;

    setExcelRows((prevRows) => prevRows.map((row) => {
      if (row.rowKey !== rowKey) return row;
      const currentOptions = row[fieldName] || [];
      if (currentOptions.some((item) => item.value === option.value)) {
        return { ...row, dirty: true };
      }
      return { ...row, [fieldName]: [...currentOptions, option], dirty: true };
    }));
  };

  const buildSalarySettingFormData = (row) => {
    const formData = new FormData();
    formData.append("user_id", row.user_id);
    formData.append("salary", unformatNumber(row.salary));
    formData.append("is_active", row.is_active ? 1 : 0);

    getIds(row.insurances).forEach((id, index) => formData.append(`insurances[${index}]`, id));
    getIds(row.facilities).forEach((id, index) => formData.append(`facilities[${index}]`, id));
    getIds(row.deductions).forEach((id, index) => formData.append(`deductions[${index}]`, id));

    if (row.id) formData.append("_method", "PUT");
    return formData;
  };

  const saveExcelRow = async (row) => {
    const salary = unformatNumber(row.salary);
    if (!salary) {
      Swal.fire({
        icon: "warning",
        title: "Salary belum diisi",
        text: `Isi salary untuk ${row.user_name} terlebih dahulu.`,
        confirmButtonColor: "#206bc4",
      });
      return false;
    }

    setSavingRows((prev) => ({ ...prev, [row.rowKey]: true }));
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/ahr/salary-settings${row.id ? `/${row.id}` : ""}`, {
        method: "POST",
        body: buildSalarySettingFormData(row),
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();

      if (!response.ok || result.success === false) {
        throw result;
      }

      setExcelRows((prevRows) => prevRows.map((item) => (
        item.rowKey === row.rowKey
          ? {
            ...item,
            id: result.data?.id || item.id,
            rowKey: result.data?.id || item.rowKey,
            dirty: false,
          }
          : item
      )));
      return true;
    } catch (error) {
      handleError(error);
      return false;
    } finally {
      setSavingRows((prev) => ({ ...prev, [row.rowKey]: false }));
    }
  };

  const saveAllExcelRows = async () => {
    const dirtyRows = excelRows.filter((row) => row.dirty);
    if (dirtyRows.length === 0) {
      handleSuccessToast(true, "Tidak ada perubahan yang perlu disimpan");
      return;
    }

    let successCount = 0;
    for (const row of dirtyRows) {
      // Simpan berurutan agar tidak membebani API saat datanya banyak.
      // eslint-disable-next-line no-await-in-loop
      const saved = await saveExcelRow(row);
      if (saved) successCount += 1;
    }

    if (successCount > 0) {
      handleSuccessToast(true, `${successCount} salary setting berhasil disimpan`);
      fetchExcelSalarySettings();
      refetch();
    }
  };

  const handleSpreadsheetKeyDown = (event) => {
    if (event.key !== "Enter") return;
    const currentCell = event.target.closest("[data-salary-cell]");
    if (!currentCell) return;

    event.preventDefault();
    const cells = Array.from(document.querySelectorAll("[data-salary-cell]"));
    const currentIndex = cells.indexOf(currentCell);
    const nextCell = cells[currentIndex + (event.shiftKey ? -1 : 1)];
    const focusTarget = nextCell?.querySelector("input, button, [tabindex]");
    if (focusTarget) {
      focusTarget.focus();
      if (typeof focusTarget.select === "function") focusTarget.select();
    }
  };

  // Create a load function that returns users from UserProvider
  const loadUserOptionsFromProvider = async (inputValue = "") => {
    if (userOptions.length > 0) {
      const filtered = userOptions.filter(user =>
        user.label.toLowerCase().includes(inputValue.toLowerCase())
      );
      return filtered;
    }
    return [];
  };

  // Form fields configuration
  const formFields = [
    {
      name: "user_id",
      label: "User",
      type: "select",
      required: true,
      loadOptions: loadUserOptionsFromProvider,
      defaultOptions: userOptions,
      placeholder: "Select user",
    },
    {
      name: "salary",
      label: "Salary",
      type: "number",
      required: true,
      placeholder: "Enter salary amount",
      isPrice: true,
    },
    {
      name: "insurances",
      label: "Insurances",
      type: "multiselect",
      loadOptions: loadInsuranceOptions,
      defaultOptions: insuranceOptions,
      placeholder: "Select insurances",
    },
    {
      name: "facilities",
      label: "Facilities",
      type: "multiselect",
      loadOptions: loadFacilityOptions,
      defaultOptions: facilityOptions,
      placeholder: "Select facilities",
    },
    {
      name: "deductions",
      label: "Deductions",
      type: "multiselect",
      loadOptions: loadDeductionOptions,
      defaultOptions: deductionOptions,
      placeholder: "Select deductions",
    },
    {
      name: "is_active",
      label: "Active",
      type: "checkbox",
      defaultValue: true,
    },
  ];

  return (
    <LayoutHR>
      {getDevelopmentBanner()}
      <div className="salary-setting-page">
        {/* Page Header */}
        <div className="page-header d-print-none">
          <div className="row align-items-center">
            <div className="col-auto">
              <button
                className="btn btn-outline-secondary"
                onClick={() => navigate(-1)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon icon-tabler icon-tabler-arrow-left"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <line x1="5" y1="12" x2="11" y2="18" />
                  <line x1="5" y1="12" x2="11" y2="6" />
                </svg>
                Back
              </button>
            </div>
            <div className="col">
              <h2 className="page-title">Salary Settings</h2>
              <div className="text-muted mt-1">
                Manage salary settings for users
              </div>
            </div>
            <div className="col-auto d-flex gap-2">
              <div className="btn-group" role="group" aria-label="Salary setting view mode">
                <button
                  type="button"
                  className={`btn ${viewMode === "list" ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => setViewMode("list")}
                >
                  List
                </button>
                <button
                  type="button"
                  className={`btn ${viewMode === "excel" ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => setViewMode("excel")}
                >
                  Excel Mode
                </button>
              </div>
              {viewMode === "list" && (
                <button
                  className="btn btn-outline-danger"
                  onClick={handleBulkDelete}
                  disabled={selectedSettingIds.length === 0}
                >
                  Delete Selected
                  {selectedSettingIds.length > 0 ? ` (${selectedSettingIds.length})` : ""}
                </button>
              )}
              <button className="btn btn-primary" onClick={handleCreate}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="icon icon-tabler icon-tabler-plus"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Setting
              </button>
            </div>
          </div>
        </div>

        {/* Search */}
        {viewMode === "list" && <div className="card mb-3">
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <Select
                  classNamePrefix="react-select"
                  placeholder="Filter by user..."
                  options={userOptions}
                  isClearable
                  value={userOptions.find(opt => opt.value === filterOptions.userId) || null}
                  onChange={(opt) => handleUserFilter(opt ? opt.value : "")}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: "38px",
                      borderRadius: "4px",
                    }),
                  }}
                />
              </div>
            </div>
          </div>
        </div>}

        {/* Table */}
        {viewMode === "excel" ? (
          <div className="card">
            <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div>
                <h3 className="card-title mb-1">Excel Mode Salary Settings</h3>
                <div className="text-muted small">
                  Edit salary banyak staff lebih cepat. Gunakan Tab atau Enter untuk pindah cell.
                </div>
              </div>
              <button
                type="button"
                className="btn btn-success"
                onClick={saveAllExcelRows}
                disabled={Object.values(savingRows).some(Boolean) || !excelRows.some((row) => row.dirty)}
              >
                {Object.values(savingRows).some(Boolean) ? "Saving..." : `Save Changes (${excelRows.filter((row) => row.dirty).length})`}
              </button>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive salary-setting-excel-wrap">
                <table className="table table-vcenter table-bordered mb-0 salary-setting-excel-table" onKeyDown={handleSpreadsheetKeyDown}>
                  <thead>
                    <tr>
                      <th style={{ minWidth: 44 }}>No</th>
                      <th style={{ minWidth: 220 }}>User</th>
                      <th style={{ minWidth: 160 }}>Department</th>
                      <th style={{ minWidth: 150 }}>Position</th>
                      <th style={{ minWidth: 150 }}>Salary</th>
                      <th style={{ minWidth: 240 }}>Insurances</th>
                      <th style={{ minWidth: 260 }}>Facilities</th>
                      <th style={{ minWidth: 240 }}>Deductions</th>
                      <th style={{ minWidth: 90 }}>Active</th>
                      <th style={{ minWidth: 110 }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(salarySettings.loading || excelSettings.loading || users.loading) ? (
                      <tr>
                        <td colSpan="10" className="text-center py-4">
                          <span className="spinner-border spinner-border-sm me-2" role="status" />
                          Loading salary settings...
                        </td>
                      </tr>
                    ) : excelRows.length > 0 ? (
                      (() => {
                        let rowNumber = 0;
                        return groupedExcelRows.map(([departmentName, rows]) => (
                          <React.Fragment key={departmentName}>
                            <tr className="salary-setting-department-row">
                              <td colSpan="10">
                                <span className="fw-bold">{departmentName}</span>
                                <span className="text-muted ms-2">
                                  {rows.length} staff, {rows.filter((row) => row.id).length} configured
                                </span>
                              </td>
                            </tr>
                            {rows.map((row) => {
                              rowNumber += 1;
                              return (
                                <tr key={row.rowKey} className={row.dirty ? "table-warning" : ""}>
                                  <td className="text-muted fw-bold">{rowNumber}</td>
                                  <td>
                                    <div className="fw-bold text-dark">{row.user_name}</div>
                                    <div className="text-muted small">{row.id ? "Configured" : "Belum disetting"}</div>
                                  </td>
                                  <td>{row.department}</td>
                                  <td>{row.position}</td>
                                  <td data-salary-cell>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm text-end"
                                      value={row.salary_display}
                                      placeholder="0"
                                      onChange={(event) => {
                                        const raw = unformatNumber(event.target.value);
                                        updateExcelRow(row.rowKey, {
                                          salary: raw,
                                          salary_display: formatNumber(raw),
                                        });
                                      }}
                                    />
                                  </td>
                                  <td data-salary-cell>
                                    <CreatableSelect
                                      classNamePrefix="react-select"
                                      isMulti
                                      options={insuranceOptions}
                                      value={row.insurances}
                                      onChange={(options) => updateExcelRow(row.rowKey, { insurances: options || [] })}
                                      onCreateOption={(inputValue) => handleCreateSalaryMasterOption(row.rowKey, "insurances", inputValue)}
                                      placeholder="Pilih asuransi"
                                      formatCreateLabel={(inputValue) => `Create insurance "${inputValue}"`}
                                      menuPortalTarget={document.body}
                                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                    />
                                  </td>
                                  <td data-salary-cell>
                                    <CreatableSelect
                                      classNamePrefix="react-select"
                                      isMulti
                                      options={facilityOptions}
                                      value={row.facilities}
                                      onChange={(options) => updateExcelRow(row.rowKey, { facilities: options || [] })}
                                      onCreateOption={(inputValue) => handleCreateSalaryMasterOption(row.rowKey, "facilities", inputValue)}
                                      placeholder="Pilih fasilitas"
                                      formatCreateLabel={(inputValue) => `Create facility "${inputValue}"`}
                                      menuPortalTarget={document.body}
                                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                    />
                                  </td>
                                  <td data-salary-cell>
                                    <CreatableSelect
                                      classNamePrefix="react-select"
                                      isMulti
                                      options={deductionOptions}
                                      value={row.deductions}
                                      onChange={(options) => updateExcelRow(row.rowKey, { deductions: options || [] })}
                                      onCreateOption={(inputValue) => handleCreateSalaryMasterOption(row.rowKey, "deductions", inputValue)}
                                      placeholder="Pilih potongan"
                                      formatCreateLabel={(inputValue) => `Create deduction "${inputValue}"`}
                                      menuPortalTarget={document.body}
                                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                                    />
                                  </td>
                                  <td data-salary-cell className="text-center">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      checked={row.is_active}
                                      onChange={(event) => updateExcelRow(row.rowKey, { is_active: event.target.checked })}
                                    />
                                  </td>
                                  <td>
                                    <button
                                      type="button"
                                      className={`btn btn-sm ${row.dirty ? "btn-primary" : "btn-outline-secondary"}`}
                                      onClick={() => saveExcelRow(row).then((saved) => saved && refetch())}
                                      disabled={savingRows[row.rowKey] || !row.dirty}
                                    >
                                      {savingRows[row.rowKey] ? "Saving..." : "Save"}
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        ));
                      })()
                    ) : (
                      <tr>
                        <td colSpan="10" className="text-center py-4">
                          No users found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-vcenter table-hover">
                <thead>
                  <tr>
                    <th className="text-center" style={{ width: 44 }}>
                      <input
                        className="form-check-input m-0"
                        type="checkbox"
                        checked={isAllVisibleSelected}
                        onChange={handleToggleSelectVisibleSettings}
                        aria-label="Select all salary settings on this page"
                      />
                    </th>
                    <th>No</th>
                    <th>Action</th>
                    <th>User</th>
                    <th>Salary</th>
                    <th>Insurances</th>
                    <th>Facilities</th>
                    <th>Deductions</th>
                  </tr>
                </thead>
                <tbody>
                  {salarySettings.loading ? (
                    <tr>
                      <td colSpan="8" className="text-center">
                        <div
                          className="spinner-border spinner-border-sm"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        Loading salary settings...
                      </td>
                    </tr>
                  ) : salarySettings.data?.length > 0 ? (
                    salarySettings.data.map((item, index) => (
                      <tr key={item.id}>
                        <td className="text-center">
                          <input
                            className="form-check-input m-0"
                            type="checkbox"
                            checked={selectedSettingIds.includes(item.id)}
                            onChange={() => handleToggleSettingSelection(item.id)}
                            aria-label={`Select salary setting ${item.user?.name || item.user_id}`}
                          />
                        </td>
                        <td>
                          {(filterOptions.currentPage - 1) * 10 + index + 1}
                        </td>
                        <td>
                          <Dropdown>
                            <Dropdown.Toggle
                              variant="default"
                              size="sm"
                              id={`dropdown-${item.id}`}
                            >
                              Actions
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item onClick={() => handleEdit(item)}>
                                Edit
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() => handleDelete(item.id)}
                                className="text-danger"
                              >
                                Delete
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                        <td>{item.user?.name || item.user_id}</td>
                        <td>Rp {new Intl.NumberFormat("id-ID").format(item.salary)}</td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            {item.insurances?.map((insurance, idx) => (
                              <small key={idx} className="badge text-white bg-info">
                                {insurance.name || insurance}
                              </small>
                            )) || "None"}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            {item.facilities?.map((facility, idx) => (
                              <small key={idx} className="badge text-white bg-success">
                                {facility.name || facility}
                              </small>
                            )) || "None"}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex flex-wrap gap-1">
                            {item.deductions?.map((deduction, idx) => (
                              <small key={idx} className="badge text-white bg-warning">
                                {deduction.name || deduction}
                              </small>
                            )) || "None"}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center">
                        No salary settings found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {salarySettings.pagination && <Pagination />}
          </div>
        </div>
        )}

        {/* Modal for Create/Edit */}
        {showModal && (
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {editingItem
                      ? "Edit Salary Setting"
                      : "Create Salary Setting"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseModal}
                  ></button>
                </div>
                <div className="modal-body">
                  <MasterDataFormV2
                    apiUrl="/ahr/salary-settings"
                    initialData={editingItem}
                    fields={formFields}
                    onSuccess={handleFormSuccess}
                    onError={handleFormError}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </LayoutHR>
  );
}

export default function SalarySettingPage() {
  return (
    <SalarySettingProvider>
      <SalarySettingContent />
    </SalarySettingProvider>
  );
}
